import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { MessageSquare, Send, Facebook, Instagram, Twitter, Linkedin, Youtube, Link2, MapPin, ChevronDown, ChevronUp, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { Reviews } from './Reviews';

interface SocialLink {
  id: string;
  platform: string;
  url: string;
  enabled: boolean;
  username?: string;
  location?: string;
}

interface BusinessProfileProps {
  businessName: string;
  profileImage: string;
  onShare: () => void;
  firstName?: string | null;
  lastName?: string | null;
  profession?: string | null;
  profileBio?: string | null;
  userId?: string | null;
  country?: string | null;
  address?: string | null;
  phoneNumber?: string | null;
  socialLinks?: SocialLink[] | null;
  preferredMessaging?: 'whatsapp' | 'telegram' | null;
  textColorClass?: string;
  hideProfileImage?: boolean;
}

export const BusinessProfile: React.FC<BusinessProfileProps> = ({
  businessName,
  profileImage,
  onShare,
  firstName,
  lastName,
  profession,
  profileBio,
  userId,
  country,
  address,
  phoneNumber,
  socialLinks,
  preferredMessaging = 'whatsapp',
  textColorClass = '',
  hideProfileImage = false
}) => {
  const { t } = useTranslation();
  const displayName = firstName && lastName 
    ? `${firstName} ${lastName}` 
    : firstName || lastName || '';

  const [fetchedSocialLinks, setFetchedSocialLinks] = useState<SocialLink[]>([]);
  const [isLoadingSocial, setIsLoadingSocial] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isReviewsOpen, setIsReviewsOpen] = useState(false);
  const [reviewCount, setReviewCount] = useState<number>(0);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);

  // Check if bio is long enough to need truncation
  const isBioLong = profileBio && profileBio.length > 500;
  
  // Create truncated and full versions of the bio
  const truncatedBio = profileBio && isBioLong ? profileBio.substring(0, 500) + '...' : profileBio;
  const displayedBio = isExpanded || !isBioLong ? profileBio : truncatedBio;

  // Use appropriate icon and colors based on the platform
  const ShareIcon = preferredMessaging === 'telegram' ? () => (
    <img 
      src="/icons8-telegram.svg" 
      alt="Telegram" 
      className="w-8 h-8" 
      style={{ filter: 'brightness(0) invert(1)' }} // Make icon white to match button text
    />
  ) : () => (
    <img 
      src="/icons8-whatsapp.svg" 
      alt="WhatsApp" 
      className="w-8 h-8" 
      style={{ filter: 'brightness(0) invert(1)' }} // Make icon white to match button text
    />
  );
  const buttonColor = preferredMessaging === 'telegram' ? 'bg-[#0088cc] hover:bg-[#006699]' : 'bg-[#25D366] hover:bg-[#128C7E]';
  const shareText = t('servicePage.shareThisPage');

  useEffect(() => {
    if (userId && !socialLinks) {
      fetchSocialLinks(userId);
    }
    if (userId) {
      fetchReviewCount(userId);
    }
  }, [userId, socialLinks]);

  const fetchReviewCount = async (userId: string) => {
    try {
      setIsLoadingReviews(true);
      
      const { count, error } = await (supabase
        .from('reviews')
        .select('id', { count: 'exact', head: true })
        .eq('service_provider_id', userId)
        .eq('is_visible', true) as any);
        
      if (error) {
        console.error('Error fetching review count:', error);
        return;
      }
      
      setReviewCount(count || 0);
    } catch (error) {
      console.error('Error in fetchReviewCount:', error);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const fetchSocialLinks = async (userId: string) => {
    try {
      setIsLoadingSocial(true);
      
      const { data, error } = await supabase
        .from('social_links')
        .select('*')
        .eq('user_id', userId)
        .eq('enabled', true)
        .returns<any[]>();
        
      if (error) {
        console.error('Error fetching social links:', error);
        return;
      }
      
      // Transform the data to match our component's expected format
      const formattedLinks: SocialLink[] = data.map(item => {
        let url = item.url;
        
        // Handle different platform types with appropriate URL formats
        if (!url && item.username) {
          if (item.platform_id === 'instagram') {
            url = `https://instagram.com/${item.username}`;
          } else if (item.platform_id === 'facebook') {
            url = `https://facebook.com/${item.username}`;
          } else if (item.platform_id === 'youtube') {
            url = `https://youtube.com/@${item.username}`;
          } else if (item.platform_id === 'linkedin') {
            url = `https://linkedin.com/in/${item.username}`;
          } else if (item.platform_id === 'tiktok') {
            url = `https://tiktok.com/@${item.username}`;
          }
        }
        
        return {
          id: item.id,
          platform: item.platform_id,
          url: url || '',
          enabled: true,
          username: item.username,
          location: item.location
        };
      }).filter(link => link.url || link.location);
      
      setFetchedSocialLinks(formattedLinks);
    } catch (error) {
      console.error('Error in fetchSocialLinks:', error);
    } finally {
      setIsLoadingSocial(false);
    }
  };

  // Use provided socialLinks prop or fetched links
  const currentSocialLinks = socialLinks || fetchedSocialLinks;
  
  // Only show links that are enabled and have a URL
  const enabledLinks = currentSocialLinks.filter(link => link.enabled && (link.url || link.location));

  const getSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'facebook':
        return <Facebook size={20} />;
      case 'instagram':
        return <Instagram size={20} />;
      case 'twitter':
        return <Twitter size={20} />;
      case 'linkedin':
        return <Linkedin size={20} />;
      case 'youtube':
        return <Youtube size={20} />;
      case 'tiktok':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
          </svg>
        );
      case 'whatsapp':
        return (
          <img 
            src="/icons8-whatsapp.svg" 
            alt="WhatsApp" 
            width="20" 
            height="20" 
            className="w-5 h-5" 
          />
        );
      case 'telegram':
        return (
          <img 
            src="/icons8-telegram.svg" 
            alt="Telegram" 
            width="20" 
            height="20" 
            className="w-5 h-5" 
          />
        );
      case 'link':
        return <Link2 size={20} />;
      case 'googlemap':
        return <MapPin size={20} />;
      default:
        return <Link2 size={20} />;
    }
  };

  // Function to get country flag image
  const getCountryFlag = (country: string) => {
    // Map of countries to their ISO codes for flag images
    const countryToCode: Record<string, string> = {
      'afghanistan': 'af',
      'albania': 'al',
      'algeria': 'dz',
      'angola': 'ao',
      'argentina': 'ar',
      'australia': 'au',
      'austria': 'at',
      'bangladesh': 'bd',
      'belgium': 'be',
      'brazil': 'br',
      'cameroon': 'cm',
      'canada': 'ca',
      'china': 'cn',
      'colombia': 'co',
      'egypt': 'eg',
      'ethiopia': 'et',
      'france': 'fr',
      'germany': 'de',
      'ghana': 'gh',
      'greece': 'gr',
      'india': 'in',
      'indonesia': 'id',
      'iran': 'ir',
      'iraq': 'iq',
      'ireland': 'ie',
      'israel': 'il',
      'italy': 'it',
      'japan': 'jp',
      'kenya': 'ke',
      'mexico': 'mx',
      'morocco': 'ma',
      'netherlands': 'nl',
      'nigeria': 'ng',
      'norway': 'no',
      'pakistan': 'pk',
      'poland': 'pl',
      'portugal': 'pt',
      'russia': 'ru',
      'saudi arabia': 'sa',
      'singapore': 'sg',
      'south africa': 'za',
      'south korea': 'kr',
      'spain': 'es',
      'sweden': 'se',
      'switzerland': 'ch',
      'tanzania': 'tz',
      'thailand': 'th',
      'turkey': 'tr',
      'uganda': 'ug',
      'ukraine': 'ua',
      'united arab emirates': 'ae',
      'united kingdom': 'gb',
      'uk': 'gb',
      'united states': 'us',
      'usa': 'us',
      'vietnam': 'vn',
    };

    const lowerCountry = country.toLowerCase().trim();
    let countryCode = '';
    
    // Try exact match
    if (countryToCode[lowerCountry]) {
      countryCode = countryToCode[lowerCountry];
    } else {
      // Try partial match
      for (const [key, code] of Object.entries(countryToCode)) {
        if (lowerCountry.includes(key) || key.includes(lowerCountry)) {
          countryCode = code;
          break;
        }
      }
    }
    
    // If we found a match, return an image, otherwise return a generic globe
    if (countryCode) {
      return (
        <img 
          src={`https://flagcdn.com/16x12/${countryCode}.png`}
          srcSet={`https://flagcdn.com/32x24/${countryCode}.png 2x, https://flagcdn.com/48x36/${countryCode}.png 3x`}
          width="16"
          height="12"
          alt={`${country} flag`}
          className="inline-block mr-1"
        />
      );
    }
    
    // Default to a globe icon if no match is found
    return (
      <span role="img" aria-label="Globe" className="mr-1">🌎</span>
    );
  };

  return (
    <div className="text-center mb-6">
      {!hideProfileImage && (
        <div className="inline-block">
          <img
            src={profileImage || '/placeholder.svg'}
            alt={businessName}
            className="w-20 h-20 md:w-24 md:h-24 rounded-full mx-auto mb-4 object-cover border-4 border-white shadow-lg"
          />
        </div>
      )}
      {businessName && (
        <h1 className={`text-2xl md:text-3xl font-bold ${textColorClass || 'text-gray-900'}`}>{businessName}</h1>
      )}
      
      {displayName && (
        <p className={`text-lg font-semibold ${textColorClass || 'text-[#403E43]'} mt-1`}>{displayName}</p>
      )}
      
      {profession && (
        <p className={`text-sm ${textColorClass || 'text-[#8E9196]'} italic mt-1`}>{profession}</p>
      )}
      
      {country && (
        <p className={`text-xs ${textColorClass || 'text-[#8E9196]'} mt-1`}>
          <span className="inline-flex items-center">
            {getCountryFlag(country)}
            {country}
          </span>
        </p>
      )}
      
      {address && (
        <p className={`text-xs ${textColorClass || 'text-[#8E9196]'} mt-1`}>{address}</p>
      )}
      
      {phoneNumber && (
        <p className={`text-xs ${textColorClass || 'text-[#8E9196]'} mt-1`}>{phoneNumber}</p>
      )}
      
      {profileBio && (
        <div className={`text-sm ${textColorClass || 'text-[#403E43]'} mt-3 mb-3 max-w-2xl mx-auto`}>
          <p style={{ whiteSpace: 'pre-line' }}>{displayedBio}</p>
          
          {isBioLong && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gebeya-orange hover:text-gebeya-pink transition-colors duration-200 flex items-center gap-1 mt-2"
            >
              {isExpanded ? (
                <>
                  {t('servicePage.showLess')}
                  <ChevronUp size={16} />
                </>
              ) : (
                <>
                  {t('servicePage.showMore')}
                  <ChevronDown size={16} />
                </>
              )}
            </button>
          )}
        </div>
      )}
      
      {/* Social Links */}
      {enabledLinks.length > 0 && (
        <div className="flex justify-center items-center mt-3 mb-4">
          {enabledLinks.map(link => (
            <a 
              key={link.id}
              href={link.platform === 'googlemap' ? `https://maps.google.com/?q=${encodeURIComponent(link.location || '')}` : link.url} 
              target="_blank"
              rel="noopener noreferrer"
              className="mx-2 p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gradient-to-r hover:from-gebeya-pink hover:to-gebeya-orange hover:text-white transition-colors"
            >
              {getSocialIcon(link.platform)}
            </a>
          ))}
        </div>
      )}
      
      <div className={`flex flex-col gap-2 mt-4 justify-center ${reviewCount > 0 ? 'sm:flex-row' : 'sm:flex-col sm:items-center'}`}>
        <Button
          onClick={onShare}
          className={`${buttonColor} text-white ${reviewCount > 0 ? 'w-full sm:w-auto' : 'w-full sm:w-64'}`}
        >
          <span className="w-8 h-8 mr-2 flex items-center justify-center">
            <ShareIcon />
          </span>
          {shareText}
        </Button>
        
        {reviewCount > 0 && (
          <Button
            onClick={() => setIsReviewsOpen(true)}
            variant="outline"
            className="w-full sm:w-auto border-gebeya-pink text-gebeya-pink hover:bg-gebeya-pink hover:text-white"
          >
            <Star className="w-4 h-4 mr-2" />
            {t('servicePage.seeReviews')}
          </Button>
        )}
      </div>
      
      {userId && (
        <Reviews
          serviceProviderId={userId}
          isOpen={isReviewsOpen}
          onClose={() => setIsReviewsOpen(false)}
        />
      )}
    </div>
  );
};
